import { alias } from '@ember/object/computed';
import { assert } from '@ember/debug';
import { attr } from '@ember-data/model';
import { observer, computed } from '@ember/object';
import Enum from '@square/dashboard-utilities/utils/enum';
import EmberDataBaseFragment from '@square/dashboard-data/models/ember-data-base-fragment';
import moment from 'moment';
import { inject as service } from '@ember/service';

// NB:- these do not need to be translated - the server expects an English string
export const DayOfWeek = new Enum(['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']);
function toMoment(timeOfDay) {
  return moment(`${timeOfDay.hour} ${timeOfDay.minute} 2013`, 'HH mm YYYY');
}
function getUnixRange(hour) {
  const start = toMoment(hour.get('startTime'));
  const end = toMoment(hour.get('endTime'));
  const dayIndex = DayOfWeek.indexOf(hour.get('dayOfWeek'));
  if (end.unix() <= start.unix()) {
    start.day(dayIndex);
    end.day((start.day() + 1) % 7); // end time goes over to next day
  } else {
    start.day(dayIndex);
    end.day(dayIndex);
  }
  return [start.unix(), end.unix()];
}
export default EmberDataBaseFragment.extend({
  store: service(),
  startTime: attr('time-of-day'),
  endTime: attr('time-of-day'),
  dayOfWeek: attr('enum', {
    enumClass: DayOfWeek
  }),
  beginTime: alias('startTime'),
  dayIndex: computed('dayOfWeek', function () {
    return DayOfWeek.indexOf(this.dayOfWeek);
  }),
  copy() {
    return this.store.createFragment('business_hours', {
      startTime: this.startTime,
      endTime: this.endTime,
      dayOfWeek: this.dayOfWeek
    });
  },
  doesOverlapWith(other) {
    if (!this.startTime || !this.endTime || !other.get('startTime') || !other.get('endTime')) {
      return false;
    }
    const [start1, end1] = getUnixRange(this);
    const [start2, end2] = getUnixRange(other);
    if (end2 <= start1) {
      return false;
    }
    return end1 > start2;
  },
  isNotDefined() {
    return !(this.startTime && this.endTime);
  },
  duration: computed('startTime', 'endTime', 'dayOfWeek', function () {
    const startTime = this.startTime;
    const endTime = this.endTime;
    if (!startTime || !endTime) {
      return null;
    }
    const time = moment.duration(toMoment(endTime) - toMoment(startTime));
    // Handle wrapping over a day.
    if (time < 0) {
      time.add(1, 'day');
    }
    const numHours = time.hours();
    const numMinutes = time.minutes();
    return {
      duration: `PT${numHours}H${numMinutes}M`,
      startTime,
      weekday: this.dayOfWeek,
      weekdayShort: this.dayOfWeek.slice(0, 2).toUpperCase()
    };
  }),
  timeToJSON() {
    const time = {
      startTime: this.startTime,
      endTime: this.endTime
    };
    return JSON.stringify(time);
  },
  // eslint-disable-next-line ember/no-observers
  _validateDayOfWeek: observer({
    sync: true,
    dependentKeys: ['dayOfWeek'],
    fn() {
      assert("Business Hours's dayOfWeek must be a DayOfWeek.", DayOfWeek.hasValue(this.dayOfWeek));
    }
  })
}).reopenClass({
  // All params must be moment objects.
  createFromStartDateAndEndDate(store, startDate, endDate) {
    const startTime = {
      hour: startDate.hours(),
      minute: startDate.minutes()
    };
    const dayOfWeek = DayOfWeek.values[startDate.weekday()];
    const endHours = endDate.hours();
    const endMinutes = endDate.minutes();
    const endTime = {
      hour: endHours,
      minute: endMinutes
    };
    return store.createFragment('business-hours', {
      startTime,
      endTime,
      dayOfWeek
    });
  },
  _modelNameForMigration: 'business-hours'
});